/**
 * LoginPage Component
 * 
 * Handles three views:
 * 1. "login" - Username/password login with Remember Me
 * 2. "setup" - First-time password setup (invite-only)
 * 3. "reset" - Forgot password with admin reset code
 * 
 * Styled to match Rich Products Malaysia branding.
 */

import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Collapse,
  Link,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";

import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
  // Current view: "login", "setup", "reset"
  const [view, setView] = useState("login");

  // Form fields
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [shake, setShake] = useState(false);

  // Auth context
  const { login, setPassword: setPasswordAction, resetPassword } = useAuth();

  // ============================================================
  // HANDLERS
  // ============================================================

  // Clear form and messages when switching views
  const switchView = (newView) => {
    setView(newView);
    setUsername("");
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setResetCode("");
    setError("");
    setSuccess("");
    setShowPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  // Trigger shake animation on error
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!username.trim() || !password) {
      setError("Please enter both username and password");
      triggerShake();
      return;
    }

    setIsSubmitting(true);

    try {
      await login(username.trim(), password, rememberMe);
      // Login successful - AuthContext will update and App.js will show dashboard
    } catch (err) {
      setError(err.message || "Invalid username or password");
      triggerShake();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle first-time password setup
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!username.trim()) {
      setError("Please enter your username");
      triggerShake();
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all password fields");
      triggerShake();
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      triggerShake();
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      triggerShake();
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await setPasswordAction(username.trim(), newPassword, confirmPassword);
      setSuccess(result.message || "Password set successfully! You can now log in.");
      // After a short delay, switch to login view
      setTimeout(() => {
        switchView("login");
        setSuccess("Password set successfully! Please log in.");
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to set password");
      triggerShake();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password reset with admin code
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!username.trim() || !resetCode.trim()) {
      setError("Please enter your username and reset code");
      triggerShake();
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all password fields");
      triggerShake();
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      triggerShake();
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      triggerShake();
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await resetPassword(username.trim(), resetCode.trim(), newPassword, confirmPassword);
      setSuccess(result.message || "Password reset successfully! You can now log in.");
      // After a short delay, switch to login view
      setTimeout(() => {
        switchView("login");
        setSuccess("Password reset successfully! Please log in.");
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to reset password");
      triggerShake();
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // PASSWORD VISIBILITY TOGGLE COMPONENT
  // ============================================================
  const PasswordToggle = ({ show, onToggle }) => (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggle}
        edge="end"
        size="small"
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  );

  // ============================================================
  // RENDER VIEWS
  // ============================================================

  // Login View
  const renderLoginView = () => (
    <Box component="form" onSubmit={handleLogin} noValidate>
      {/* Icon */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LockOutlinedIcon sx={{ color: "white", fontSize: 24 }} />
        </Box>
      </Box>

      <Typography
        variant="h2"
        align="center"
        sx={{ mb: 0.5, fontWeight: 600 }}
      >
        Welcome Back
      </Typography>
      <Typography
        variant="body2"
        align="center"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Sign in to manage your short URLs
      </Typography>

      {/* Username */}
      <TextField
        fullWidth
        label="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        disabled={isSubmitting}
        autoFocus
        autoComplete="username"
        sx={{ mb: 2 }}
      />

      {/* Password */}
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={isSubmitting}
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <PasswordToggle
              show={showPassword}
              onToggle={() => setShowPassword(!showPassword)}
            />
          ),
        }}
        sx={{ mb: 1 }}
      />

      {/* Remember Me */}
      <FormControlLabel
        control={
          <Checkbox
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            size="small"
            disabled={isSubmitting}
            sx={{
              color: "grey.400",
              "&.Mui-checked": {
                color: "primary.main",
              },
            }}
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            Remember me
          </Typography>
        }
        sx={{ mb: 2, ml: -0.5 }}
      />

      {/* Login Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={isSubmitting}
        sx={{
          py: 1.5,
          mb: 3,
          fontWeight: 600,
          fontSize: "15px",
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Log In"
        )}
      </Button>

      {/* Links */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => switchView("reset")}
          sx={{
            color: "text.secondary",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              color: "primary.main",
              textDecoration: "underline",
            },
          }}
        >
          Forgot password?
        </Link>

        <Box
          sx={{
            width: "100%",
            borderTop: "1px solid",
            borderColor: "grey.200",
            pt: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" component="span">
            First time?{" "}
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => switchView("setup")}
            sx={{
              color: "primary.main",
              fontWeight: 500,
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Set up your password
          </Link>
        </Box>
      </Box>
    </Box>
  );

  // Setup Password View
  const renderSetupView = () => (
    <Box component="form" onSubmit={handleSetPassword} noValidate>
      {/* Icon */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PersonAddOutlinedIcon sx={{ color: "white", fontSize: 24 }} />
        </Box>
      </Box>

      <Typography
        variant="h2"
        align="center"
        sx={{ mb: 0.5, fontWeight: 600 }}
      >
        Set Up Your Password
      </Typography>
      <Typography
        variant="body2"
        align="center"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Enter the username provided by your administrator
      </Typography>

      {/* Username */}
      <TextField
        fullWidth
        label="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        disabled={isSubmitting}
        autoFocus
        autoComplete="username"
        sx={{ mb: 2 }}
      />

      {/* New Password */}
      <TextField
        fullWidth
        label="New Password"
        type={showNewPassword ? "text" : "password"}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={isSubmitting}
        autoComplete="new-password"
        helperText="Minimum 6 characters"
        InputProps={{
          endAdornment: (
            <PasswordToggle
              show={showNewPassword}
              onToggle={() => setShowNewPassword(!showNewPassword)}
            />
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Confirm Password */}
      <TextField
        fullWidth
        label="Confirm Password"
        type={showConfirmPassword ? "text" : "password"}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={isSubmitting}
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <PasswordToggle
              show={showConfirmPassword}
              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            />
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Set Password Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={isSubmitting}
        sx={{
          py: 1.5,
          mb: 3,
          fontWeight: 600,
          fontSize: "15px",
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Set Password"
        )}
      </Button>

      {/* Back to Login */}
      <Box sx={{ textAlign: "center" }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => switchView("login")}
          sx={{
            color: "text.secondary",
            textDecoration: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": {
              color: "primary.main",
              textDecoration: "underline",
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to Login
        </Link>
      </Box>
    </Box>
  );

  // Reset Password View
  const renderResetView = () => (
    <Box component="form" onSubmit={handleResetPassword} noValidate>
      {/* Icon */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LockResetIcon sx={{ color: "white", fontSize: 24 }} />
        </Box>
      </Box>

      <Typography
        variant="h2"
        align="center"
        sx={{ mb: 0.5, fontWeight: 600 }}
      >
        Reset Your Password
      </Typography>
      <Typography
        variant="body2"
        align="center"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Enter the reset code provided by your administrator
      </Typography>

      {/* Username */}
      <TextField
        fullWidth
        label="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        disabled={isSubmitting}
        autoFocus
        autoComplete="username"
        sx={{ mb: 2 }}
      />

      {/* Reset Code */}
      <TextField
        fullWidth
        label="Reset Code"
        value={resetCode}
        onChange={(e) => setResetCode(e.target.value)}
        disabled={isSubmitting}
        placeholder="Enter code from admin"
        sx={{ mb: 2 }}
      />

      {/* New Password */}
      <TextField
        fullWidth
        label="New Password"
        type={showNewPassword ? "text" : "password"}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={isSubmitting}
        autoComplete="new-password"
        helperText="Minimum 6 characters"
        InputProps={{
          endAdornment: (
            <PasswordToggle
              show={showNewPassword}
              onToggle={() => setShowNewPassword(!showNewPassword)}
            />
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Confirm Password */}
      <TextField
        fullWidth
        label="Confirm Password"
        type={showConfirmPassword ? "text" : "password"}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={isSubmitting}
        autoComplete="new-password"
        InputProps={{
          endAdornment: (
            <PasswordToggle
              show={showConfirmPassword}
              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            />
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Reset Password Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={isSubmitting}
        sx={{
          py: 1.5,
          mb: 3,
          fontWeight: 600,
          fontSize: "15px",
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Reset Password"
        )}
      </Button>

      {/* Back to Login */}
      <Box sx={{ textAlign: "center" }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => switchView("login")}
          sx={{
            color: "text.secondary",
            textDecoration: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": {
              color: "primary.main",
              textDecoration: "underline",
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to Login
        </Link>
      </Box>
    </Box>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        backgroundImage: "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
        padding: 2,
      }}
    >
      {/* Login Card */}
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: "16px",
          overflow: "hidden",
          animation: shake ? "shake 0.5s ease-in-out" : "none",
          "@keyframes shake": {
            "0%, 100%": { transform: "translateX(0)" },
            "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-4px)" },
            "20%, 40%, 60%, 80%": { transform: "translateX(4px)" },
          },
        }}
      >
        {/* Red accent bar at top */}
        <Box
          sx={{
            height: 4,
            backgroundColor: "primary.main",
          }}
        />

        <Box sx={{ p: 4 }}>
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              component="img"
              src="https://wiki.richproductsmy.kitchen/wp-content/uploads/2025/03/richs_logo_lockup_horiz.png"
              alt="Rich Products Malaysia"
              sx={{
                height: 60,
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Error Message */}
          <Collapse in={!!error}>
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{ mb: 2, borderRadius: "8px" }}
            >
              {error}
            </Alert>
          </Collapse>

          {/* Success Message */}
          <Collapse in={!!success}>
            <Alert
              severity="success"
              onClose={() => setSuccess("")}
              sx={{ mb: 2, borderRadius: "8px" }}
            >
              {success}
            </Alert>
          </Collapse>

          {/* Render active view */}
          {view === "login" && renderLoginView()}
          {view === "setup" && renderSetupView()}
          {view === "reset" && renderResetView()}
        </Box>
      </Paper>

      {/* Footer */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 3, fontSize: "12px" }}
      >
        © {new Date().getFullYear()} Rich Products Malaysia Sdn Bhd
      </Typography>
    </Box>
  );
};

export default LoginPage;
